/**
 * Bảng định tuyến. Đường dẫn để bằng tiếng Việt không dấu cho dễ đọc, dễ đọc lên điện thoại.
 *
 *   /                  Tổng quan
 *   /tu/:id            Chi tiết tủ
 *   /dieu-khien        Điều khiển toàn tuyến
 *   /lich[/:cabinetId] Lịch đóng cắt
 *   /chien-luoc        Chiến lược
 *   /nguoi-dung        Quản trị người dùng   (ADMIN trở lên)
 *   /nhat-ky           Nhật ký thao tác      (ADMIN trở lên)
 */

import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './lib/auth';
import Layout, { ChangePasswordModal } from './components/Layout';
import { Spinner } from './components/ui';
import type { Role } from './lib/types';
import { NHAN_KHOA, NHAN_CO_TUYEN } from './lib/quyen';

import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import CabinetDetailPage from './pages/CabinetDetail';
import RouteControlPage from './pages/RouteControl';
import CaiDatChienLuocPage from './pages/CaiDatChienLuoc';
import CaiDatBanDauPage from './pages/CaiDatBanDau';
import ConDuongPage from './pages/ConDuong';
import QuanTriPage from './pages/QuanTri';
import AuditPage from './pages/Audit';
import BanDoPage from './pages/BanDo';
import DenNemaPage from './pages/DenNema';
import VanDePage from './pages/VanDe';
import DienNangPage from './pages/DienNang';

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Đang kiểm tra phiên đăng nhập…" />
      </div>
    );
  }
  if (!user) return <Navigate to="/dang-nhap" state={{ from: location.pathname }} replace />;

  return (
    <>
      {children}
      {/* Mật khẩu tạm do quản trị cấp → bắt buộc đổi, không cho đóng hộp thoại */}
      <ChangePasswordModal open={user.mustChangePwd} onClose={() => { /* bắt buộc */ }} forced />
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `P-6` `B4` mục 3 — CỬA TRANG theo TICK, và một cửa theo CỜ TUYẾN.
 *
 * ⛔ Đây vẫn KHÔNG phải bảo mật: người dùng gõ thẳng địa chỉ vẫn tải được mã trang; thứ chặn
 *   thật là máy chủ ở từng lời gọi. Cửa này chỉ để **không dẫn người ta vào một trang mà mọi
 *   nút trong đó sẽ trả 403** — và để câu từ chối nói đúng lý do.
 */
/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-09` mục 4 — TRANG ĐÍCH CỦA MỘT MỤC MENU BỊ KHOÁ. Từ bản này mục khoá **vẫn hiện** ở
 * thanh bên (khoá-không-ẩn), nên trang này là chỗ người ta thật sự đọc câu giải thích — nó
 * thôi là một ngõ cụt hiếm gặp và thành một phần của đường đi.
 *
 * 🔴 HAI CHỖ SỬA, cả hai đều là lỗi CŨ mà chỉ lộ ra khi đường đi này trở nên thường xuyên:
 *   ① `text-slate-800` / `text-slate-500` là **mã màu của Tailwind, không phải token của
 *      `thietke.css`** — ở bộ TỐI nó là chữ đậm trên nền đậm, gần như không đọc được. Cùng họ
 *      với lỗi `GD-09` mục 7 (huy hiệu mờ ở bộ sáng): một màu không đi qua token là một màu
 *      chỉ đúng ở đúng một chế độ.
 *   ② `coQuyen(khoa)` trả `false` khi `/api/auth/me` CHƯA trả lời, nên trang này chớp một câu
 *      *"Không đủ quyền"* rồi mới đổi ý. Nó khai một điều chưa đo được — Luật số 0, cùng gốc
 *      với mục 3 và 4. ⇒ chưa biết thì nói "đang kiểm", không kết tội.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function RequireQuyen({ khoa, children }: { khoa: string; children: ReactElement }) {
  const { coQuyen, daBietQuyen } = useAuth();
  if (!daBietQuyen) {
    return (
      <div className="card p-6 text-center">
        <p className="text-base font-semibold text-chu-2">Đang kiểm quyền…</p>
        <p className="mt-1 text-sm text-chu-3">Đang hỏi máy chủ xem tài khoản của bạn có quyền này không.</p>
      </div>
    );
  }
  if (!coQuyen(khoa)) {
    return (
      <div className="card p-6 text-center">
        <p className="text-base font-semibold text-chu-1">Không đủ quyền truy cập</p>
        <p className="mt-1 text-sm text-chu-2">
          Tài khoản của bạn không có quyền “{NHAN_KHOA[khoa] ?? khoa}”. Người tạo tài khoản cho
          bạn có thể bật quyền này trong màn hình Quản trị.
        </p>
      </div>
    );
  }
  return children;
}

/** Cần ÍT NHẤT một tuyến mang cờ ấy — hạng và tick đều không trả lời được câu này. */
function RequireCoTuyenBatKy({ co, children }: { co: 'canControl' | 'canEditSchedule'; children: ReactElement }) {
  const { user, daBietQuyen } = useAuth();
  /* ⛔ `GD-09` — cùng luật với `RequireQuyen` ngay trên: chưa hỏi xong thì chưa kết luận. */
  if (!daBietQuyen) {
    return (
      <div className="card p-6 text-center">
        <p className="text-base font-semibold text-chu-2">Đang kiểm quyền…</p>
        <p className="mt-1 text-sm text-chu-3">Đang hỏi máy chủ xem bạn được gán tuyến nào.</p>
      </div>
    );
  }
  const co1 = user?.role === 'SUPER_ADMIN' || (user?.routeAccess ?? []).some((r) => r[co]);
  if (!co1) {
    return (
      <div className="card p-6 text-center">
        <p className="text-base font-semibold text-chu-1">Chưa được gán tuyến nào</p>
        <p className="mt-1 text-sm text-chu-2">
          Tài khoản của bạn chưa được gán tuyến nào có quyền “{NHAN_CO_TUYEN[co]}”.
          Quản trị viên cao nhất là người gán tuyến.
        </p>
      </div>
    );
  }
  return children;
}

function RequireRole({ role, children }: { role: Role; children: ReactElement }) {
  const { hasRole } = useAuth();
  if (!hasRole(role)) {
    return (
      <div className="card p-6 text-center">
        <p className="text-base font-semibold text-chu-1">Không đủ quyền truy cập</p>
        <p className="mt-1 text-sm text-chu-2">
          Khu vực này dành cho tài khoản có quyền cao hơn. Liên hệ quản trị viên nếu bạn cần dùng.
        </p>
      </div>
    );
  }
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/dang-nhap" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="/tu/:id" element={<CabinetDetailPage />} />
        <Route path="/dieu-khien" element={<RequireCoTuyenBatKy co="canControl"><RouteControlPage /></RequireCoTuyenBatKy>} />
        {/* TK-39 §1: một trang Cài đặt chiến lược; đường cũ chuyển hướng (link cũ không gãy) */}
        <Route path="/cai-dat-chien-luoc" element={<CaiDatChienLuocPage />} />
        {/* TK-40: chỉ SUPER_ADMIN + ADMIN (lee Q3) — hạng khác vào thẳng URL gặp khối "Không đủ quyền" */}
        <Route path="/cai-dat-ban-dau" element={<RequireRole role="ADMIN"><CaiDatBanDauPage /></RequireRole>} />
        <Route path="/lich" element={<Navigate to="/cai-dat-chien-luoc?tab=dong-cat" replace />} />
        <Route path="/lich/:cabinetId" element={<ChuyenLichTu />} />
        <Route path="/chien-luoc" element={<Navigate to="/cai-dat-chien-luoc?tab=dong-cat&mau=1" replace />} />
        <Route path="/con-duong" element={<RequireQuyen khoa="quan_ly_tu"><ConDuongPage /></RequireQuyen>} />
        <Route path="/quan-tri" element={<RequireQuyen khoa="tao_tai_khoan"><QuanTriPage /></RequireQuyen>} />
        {/* ⚠ Địa chỉ CŨ giữ lại và chuyển hướng: người ta đã lưu dấu trang, và một liên kết cũ
            trả về "không tìm thấy" là cách tệ nhất để đổi tên một màn hình. */}
        <Route path="/nguoi-dung" element={<Navigate to="/quan-tri" replace />} />
        {/*
          * ➕ `v4` `S6` — KHÔNG CÒN `RequireQuyen` ở đây. Trang tự co phạm vi: có tick thì cả
          *   nhật ký, không có thì **chỉ việc của chính mình** (máy chủ ép `userId = tôi`).
          * ⛔ Bỏ cổng ở đây KHÔNG phải bỏ cổng: cửa thật ở `requireQuyenHoacCuaToi` tại máy chủ.
          *   Giữ `RequireQuyen` lại thì trang bị chặn TRƯỚC khi ai kịp hỏi "của tôi thì sao".
          */}
        <Route path="/nhat-ky" element={<AuditPage />} />
        {/* GD-17 B3 — "Xem toàn màn hình" của bản đồ tủ. KHÔNG có mục điều hướng. */}
        <Route path="/ban-do" element={<BanDoPage />} />
        {/* TK-31d / TK-32 (22/10) — trang chỉ có nghĩa khi máy chủ bật NEMA (trang tự kiểm `useNemaBat`); mục menu "Đèn"
          * nằm ở `MUC_NEMA` (chỉ khi cờ bật). TK-35 lượt 2: /nhom-den (nhóm theo tuyến) BỎ — nhóm/lịch dim sống trong tab Đèn NEMA
          * của tủ; /den-nema = danh sách tủ có đèn. */}
        <Route path="/den-nema" element={<DenNemaPage />} />
        <Route path="/van-de" element={<VanDePage />} />
        {/* TK-33b: trang Điện năng (mục menu thứ 6 của mẫu) — đọc `/api/dien-nang/ngay`, phạm vi tài khoản. */}
        <Route path="/dien-nang" element={<DienNangPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** TK-39 §1: `/lich/:cabinetId` cũ ⇒ tab Đóng cắt của tủ ấy. */
function ChuyenLichTu() {
  const { cabinetId } = useParams();
  return <Navigate to={`/cai-dat-chien-luoc?tab=dong-cat&tu=${encodeURIComponent(cabinetId ?? '')}`} replace />;
}
